// components/features/users/UserRow.tsx
import { User } from "@cabukaka/types";
import TableRow from "../../ui/TableRow";
import { User as UserIcon, Mail, Shield, Calendar } from "lucide-react";

interface UserRowProps {
  user: User;
  onAction: (user: User) => void;
  onSelect?: (user: User, isSelected: boolean) => void;
  isSelected?: boolean;
  selectable?: boolean;
}

export default function UserRow({ 
  user, 
  onAction, 
  onSelect,
  isSelected = false,
  selectable = false
}: UserRowProps) {
  const getRoleBadge = (role: string) => {
    switch (role) {
      case "admin":
        return <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700">Admin</span>;
      case "shop":
        return <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-700">Vendeur</span>;
      case "client":
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Client</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">{role}</span>;
    }
  };

  return (
    <TableRow
      item={user}
      onAction={onAction}
      onSelect={onSelect}
      isSelected={isSelected}
      selectable={selectable}
      actionable={true}
    >
      {/* Colonne Utilisateur - toujours visible */}
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            {user.avatar ? (
              <img src={user.avatar} alt={user.username} className="w-full h-full rounded-full object-cover" />
            ) : (
              <UserIcon size={18} className="text-primary" />
            )}
          </div>
          <div>
            <span className="font-medium text-gray-800 block">
              {user.username}
            </span>
            <div className="flex items-center gap-2 mt-0.5">
              <Mail size={12} className="text-gray-400" />
              <span className="text-xs text-gray-500">{user.email}</span>
            </div>
          </div>
        </div>
      </td>

      {/* Colonne Rôle - cachée sur mobile */}
      <td className="hidden sm:table-cell py-3 px-4">
        {getRoleBadge(user.role)}
      </td>

      {/* Colonne Statut - cachée sur mobile */}
      <td className="hidden sm:table-cell py-3 px-4">
        <span className={`text-xs ${user.isVerified ? 'text-emerald-600' : 'text-amber-600'}`}>
          {user.isVerified ? 'Vérifié' : 'Non vérifié'}
        </span>
      </td>

      {/* Colonne Date création - cachée sur tablette */}
      <td className="hidden md:table-cell py-3 px-4">
        <div className="flex items-center gap-2">
          <Calendar size={14} className="text-gray-400" />
          <span className="text-gray-600 text-sm">
            {new Date(user.createdAt).toLocaleDateString('fr-FR')}
          </span>
        </div>
      </td>
    </TableRow>
  );
}